/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { View, BoxView, ActionButton, Text, TextField } from '@components/atoms'
import { Grid, dispatchColumns } from '@grid'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import {
	Select,
	SelectChangeEvent,
	MenuItem,
	FormControl,
	InputLabel,
} from '@mui/material'
import {
	GridRenderCellParams,
	GridRowSelectionModel,
	GridRowParams,
} from '@mui/x-data-grid'
import {
	useAppSelector,
	useAppDispatch,
	shipmentActions,
	routeActions,
	ApiGetDataStatus,
} from '@redux/index'
import { ShipmentSearchMode, ShipmentStateType } from '@redux/types'
import { DispatchType, SeasonType } from '@schemas'
import {
	ShipmentScanModal,
	ShipmentMessageModal,
	DispatchDetail,
	DispatchCreate,
	ShipmentLabelCreate,
	ShipmentLabelView,
} from '@components/systems'
import {
	mono,
	boShared,
	boStatusChipClass,
} from '@components/systems/backoffice/backoffice.tokens'
import {
	DISPATCH_KIND_OPTIONS,
	DISPATCH_KIND_TITLE,
	DISPATCH_STATUS_FILTER_OPTIONS,
	DISPATCH_STATUS_TITLE,
	dispatchTrackUrl,
} from '@src/configs/dispatch.config'
import dayjs from 'dayjs'

const money = (currency: string, value: number | null): string => {
	if (value === null) return ''
	return currency === 'KRW'
		? `₩${Math.round(value).toLocaleString('ko-KR')}`
		: `$${value.toLocaleString('en-US', {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
		  })}`
}

/**
 * 배송 관리 = 출고 대장 (DISPATCH_PLAN.md §3). **행 하나가 배송 건(박스) 하나**다 — 주문확인 때 자동으로
 * 생긴 판매·도매 건과 손으로 넣은 수리·광고·입고 건이 한 표에 선다. 기본 기준은 월(주문일) = 엑셀 월 탭.
 * 라벨 생성 · 라벨 인쇄 · 추적 갱신 · 배송 건 추가를 이 화면에서 한다. 행을 클릭하면 스캔 이력(라벨이 있을 때).
 */
const ShippingPage = () => {
	const reduxDispatch = useAppDispatch()

	/*
		라우트가 다섯이다 — 목록 · 배송 건 추가 · 자세히 · 라벨 만들기 · 라벨 보기. 그리는 것은 같은 페이지이고,
		주소의 배송 건 ID 와 모드만 옮겨 적는다. **열림/닫힘의 정본은 URL** 이다(오더 관리와 같은 방식).
		  /main/shipping                            목록
		  /main/shipping/new                        배송 건 추가(수동)
		  /main/shipping/:dispatchId                자세히
		  /main/shipping/:dispatchId/label/new      라벨 만들기
		  /main/shipping/:dispatchId/label          라벨 보기(활성 건)
		  /main/shipping/:dispatchId/label/:id      라벨 보기(이력의 특정 건)
	*/
	const { dispatchId: routeDispatchId, shipmentId: routeShipmentId } = useParams()
	const { pathname } = useLocation()
	const isCreateRoute: boolean = pathname === '/main/shipping/new'
	const dispatchId: string = isCreateRoute ? '' : routeDispatchId ?? ''
	const isLabelRoute: boolean = pathname.includes('/label')
	const isLabelCreate: boolean = pathname.endsWith('/label/new')
	const isLabelView: boolean = isLabelRoute && !isLabelCreate

	const detailDispatchId: string = useAppSelector(
		state => state.shipment.detailDispatchId,
	)
	const labelShipmentId: string = useAppSelector(state => state.shipment.labelShipmentId)

	// URL → 상태. 배송 건이 바뀌면 자세히를 다시 읽는다
	React.useEffect(() => {
		if (dispatchId !== detailDispatchId) {
			reduxDispatch(shipmentActions.DETAIL_DISPATCH_ID(dispatchId))
		}
		if (dispatchId !== '') {
			reduxDispatch(shipmentActions.FETCH_DETAIL(dispatchId))
		}
	}, [dispatchId, detailDispatchId, reduxDispatch])

	React.useEffect(() => {
		const next: string = isLabelView ? routeShipmentId ?? '' : ''
		if (next !== labelShipmentId) {
			reduxDispatch(shipmentActions.LABEL_SHIPMENT_ID(next))
		}
	}, [isLabelView, routeShipmentId, labelShipmentId, reduxDispatch])

	const list: DispatchType[] = useAppSelector(state => state.shipment.shipmentList)
	const status: ApiGetDataStatus = useAppSelector(
		state => state.shipment.shipmentListStatus,
	)
	const searchStatus = useAppSelector(state => state.shipment.searchStatus)
	const searchKeyword: string = useAppSelector(state => state.shipment.searchKeyword)
	const searchMode: ShipmentSearchMode = useAppSelector(
		state => state.shipment.searchMode,
	)
	const searchMonth: string = useAppSelector(state => state.shipment.searchMonth)
	const searchSeasonId: string = useAppSelector(state => state.shipment.searchSeasonId)
	const seasonOptions: SeasonType[] = useAppSelector(
		state => state.shipment.seasonOptions,
	)
	const searchStartDate: string = useAppSelector(state => state.shipment.searchStartDate)
	const searchEndDate: string = useAppSelector(state => state.shipment.searchEndDate)
	const searchKind: string = useAppSelector(state => state.shipment.searchKind)
	const searchChannel = useAppSelector(state => state.shipment.searchChannel)
	const selectedIds: string[] = useAppSelector(
		state => state.shipment.selectedShipmentIds,
	)
	const tracking: boolean = useAppSelector(state => state.shipment.tracking)

	const selected = React.useMemo(
		() => list.filter(s => selectedIds.includes(s.id)),
		[list, selectedIds],
	)

	/** 선택한 배송 건 중 FedEx 라벨이 있는 것만 추적한다. 선택이 없으면 서버가 배송중 전체를 돈다 */
	const trackable = React.useMemo(
		() => selected.filter(s => s.shipmentId !== '').map(s => s.id),
		[selected],
	)

	const onTrackClick = () => {
		reduxDispatch(
			shipmentActions.TRIGGER_TRACK({
				shipmentIds: [],
				orderIds: [],
				dispatchIds: trackable,
				refreshOrders: false,
			}),
		)
	}

	/** 라벨 생성 — 송장이 없는 배송 건 하나. 폼은 자기 주소를 가진 화면이다 */
	const onLabelCreateClick = () => {
		if (selected.length !== 1) return
		reduxDispatch(
			routeActions.NAVIGATE_PATH(
				`/main/shipping/${encodeURIComponent(selected[0].id)}/label/new`,
			),
		)
	}

	const onMonthChange = (date: any) => {
		const d = dayjs(date)
		if (d.isValid()) reduxDispatch(shipmentActions.SEARCH_MONTH(d.format('YYYY-MM')))
	}

	const onStartDateChange = (date: any) => {
		reduxDispatch(shipmentActions.SEARCH_START_DATE(dayjs(date).format('YYYY-MM-DD')))
	}

	const onEndDateChange = (date: any) => {
		reduxDispatch(shipmentActions.SEARCH_END_DATE(dayjs(date).format('YYYY-MM-DD')))
	}

	const onPrintClick = () => {
		if (selected.length !== 1) return
		reduxDispatch(
			shipmentActions.TRIGGER_PRINT_LABEL({
				shipmentId: selected[0].shipmentId,
				orderId: selected[0].orderId,
			}),
		)
	}

	return (
		<View css={style}>
			<BoxView
				title="배송 관리"
				className="shippingBoxView"
				leftHeaderAccessory={
					<View className="leftView">
						<button
							type="button"
							className="boBtn"
							onClick={() => reduxDispatch(shipmentActions.TRIGGER_SHIPMENT_SEARCH(true))}
						>
							검색
						</button>

						{/* 검색 기준 — 월(주문일, 기본) | 기간(주문일) | 시즌. 오더 관리와 같은 구성이고 값은 독립이다 */}
						<FormControl>
							<InputLabel id="shipment-search-mode-label">기준</InputLabel>
							<Select
								className="modeSelect"
								labelId="shipment-search-mode-label"
								label="기준"
								value={searchMode}
								size="small"
								onChange={(e: SelectChangeEvent) =>
									reduxDispatch(
										shipmentActions.SEARCH_MODE(e.target.value as ShipmentSearchMode),
									)
								}
							>
								<MenuItem value="month">월</MenuItem>
								<MenuItem value="date">기간</MenuItem>
								<MenuItem value="season">시즌</MenuItem>
							</Select>
						</FormControl>

						{searchMode === 'season' ? (
							<FormControl>
								<InputLabel id="shipment-season-select-label" shrink>
									시즌
								</InputLabel>
								<Select
									className="seasonSelect"
									labelId="shipment-season-select-label"
									label="시즌"
									value={searchSeasonId}
									size="small"
									displayEmpty
									onChange={(e: SelectChangeEvent) =>
										reduxDispatch(shipmentActions.SEARCH_SEASON_ID(e.target.value))
									}
								>
									<MenuItem value="">전체 시즌</MenuItem>
									{seasonOptions.map(s => (
										<MenuItem key={s.id} value={s.id}>
											{s.title}
											<span style={{ marginLeft: 8, fontSize: 11, color: mono.faint }}>
												{s.startDate.slice(0, 10)}
											</span>
										</MenuItem>
									))}
								</Select>
							</FormControl>
						) : searchMode === 'month' ? (
							<LocalizationProvider dateAdapter={AdapterDateFns}>
								<View className="datePickerView">
									<DatePicker
										className="datePicker"
										label="월"
										views={['year', 'month']}
										openTo="month"
										value={new Date(`${searchMonth}-01`)}
										format="yyyy-MM"
										onChange={onMonthChange}
										slotProps={{
											textField: {
												variant: 'outlined',
												size: 'small',
												style: { width: '8rem' },
												placeholder: 'YYYY-MM',
											},
										}}
									/>
								</View>
							</LocalizationProvider>
						) : (
							<LocalizationProvider dateAdapter={AdapterDateFns}>
								<View className="datePickerView">
									<DatePicker
										className="datePicker"
										label="시작일자"
										value={new Date(searchStartDate)}
										format="yyyy-MM-dd"
										onChange={onStartDateChange}
										slotProps={{
											textField: {
												variant: 'outlined',
												size: 'small',
												style: { width: '8rem' },
												placeholder: 'YYYY-MM-DD',
											},
										}}
									/>
									<Text className="datePickerText">~</Text>
									<DatePicker
										className="datePicker"
										label="종료일자"
										format="yyyy-MM-dd"
										value={new Date(searchEndDate)}
										onChange={onEndDateChange}
										slotProps={{
											textField: {
												variant: 'outlined',
												size: 'small',
												style: { width: '8rem' },
												placeholder: 'YYYY-MM-DD',
											},
										}}
									/>
								</View>
							</LocalizationProvider>
						)}

						<FormControl>
							<InputLabel id="shipment-kind-select-label">종류</InputLabel>
							<Select
								className="kindSelect"
								labelId="shipment-kind-select-label"
								label="종류"
								value={searchKind}
								size="small"
								onChange={(e: SelectChangeEvent) =>
									reduxDispatch(shipmentActions.SEARCH_KIND(e.target.value))
								}
							>
								<MenuItem value="all">전체</MenuItem>
								{DISPATCH_KIND_OPTIONS.map(k => (
									<MenuItem key={k.value} value={k.value}>
										{k.label}
									</MenuItem>
								))}
							</Select>
						</FormControl>

						<FormControl>
							<InputLabel id="shipment-channel-select-label">채널</InputLabel>
							<Select
								className="channelSelect"
								labelId="shipment-channel-select-label"
								label="채널"
								value={searchChannel}
								size="small"
								onChange={(e: SelectChangeEvent) =>
									reduxDispatch(
										shipmentActions.SEARCH_CHANNEL(
											e.target.value as 'all' | 'retail' | 'wholesale',
										),
									)
								}
							>
								<MenuItem value="all">전체</MenuItem>
								<MenuItem value="retail">소매</MenuItem>
								<MenuItem value="wholesale">도매</MenuItem>
							</Select>
						</FormControl>

						<FormControl>
							<InputLabel id="shipment-status-select-label">상태</InputLabel>
							<Select
								className="statusSelect"
								labelId="shipment-status-select-label"
								label="상태"
								value={searchStatus}
								size="small"
								onChange={(e: SelectChangeEvent) =>
									reduxDispatch(
										shipmentActions.SEARCH_STATUS(
											e.target.value as ShipmentStateType['searchStatus'],
										),
									)
								}
							>
								{DISPATCH_STATUS_FILTER_OPTIONS.map(o => (
									<MenuItem key={o.value} value={o.value}>
										{o.label}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						<TextField
							className="keywordField"
							type="text"
							label="검색"
							placeholder="주문번호 · 송장 · 수취인 · 거래처 · S/N"
							size="small"
							value={searchKeyword}
							onChange={e =>
								reduxDispatch(shipmentActions.SEARCH_KEYWORD(e.target.value))
							}
							onKeyDown={e => {
								if (e.key === 'Enter')
									reduxDispatch(shipmentActions.TRIGGER_SHIPMENT_SEARCH(true))
							}}
						/>
					</View>
				}
				rightHeaderAccessory={
					<View className="rightView">
						<ActionButton
							onClick={onTrackClick}
							title={
								tracking
									? '조회 중…'
									: trackable.length > 0
									? `추적 갱신 (${trackable.length})`
									: '추적 갱신 (배송중 전체)'
							}
							// 선택했는데 그중 라벨이 하나도 없으면 누를 것이 없다
							disabled={tracking || (selected.length > 0 && trackable.length === 0)}
							iconName="refresh"
						/>
						<ActionButton
							onClick={onLabelCreateClick}
							title="라벨 생성"
							disabled={selected.length !== 1 || selected[0].trackingNumber !== ''}
							iconName="printer-pos-outline"
						/>
						<ActionButton
							onClick={onPrintClick}
							title="라벨 인쇄"
							disabled={selected.length !== 1 || selected[0].hasLabel !== 1}
							iconName="printer-pos-outline"
						/>
						{/* 수동 건 — 수리 · 광고 · 교체 · 지원 · 외주 · 입고. 주문 없는 판매도 여기서 */}
						<ActionButton
							onClick={() =>
								reduxDispatch(routeActions.NAVIGATE_PATH('/main/shipping/new'))
							}
							title="배송 건 추가"
							iconName="plus"
						/>
					</View>
				}
			>
				<Grid
					title="배송 관리"
					rows={list}
					columns={dispatchColumns}
					loading={status === 'loading'}
					isEditable={false}
					checkboxSelection={true}
					rowSelectionModel={selectedIds}
					onRowSelectionModelChange={(m: GridRowSelectionModel) =>
						reduxDispatch(shipmentActions.SELECTED_SHIPMENT_IDS(m as string[]))
					}
					onDetailClick={row =>
						reduxDispatch(
							routeActions.NAVIGATE_PATH(
								`/main/shipping/${encodeURIComponent(String(row.id))}`,
							),
						)
					}
					onRowClick={(params: GridRowParams) => {
						// 스캔 이력은 FedEx 라벨이 있을 때만. 국내 택배·라벨 전 건은 볼 것이 없다
						const shipmentId = String((params.row as DispatchType).shipmentId ?? '')
						if (shipmentId === '') return
						reduxDispatch(shipmentActions.SCAN_MODAL_SHIPMENT_ID(shipmentId))
					}}
					renderCell={{
						kind: (params: GridRenderCellParams<DispatchType, any, any>) => (
							<span
								className={`boChip${
									params.row.direction === 'in'
										? ' faint'
										: params.row.orderId !== ''
										? ' outline'
										: ''
								}`}
							>
								{DISPATCH_KIND_TITLE[params.value] ?? params.value}
							</span>
						),
						// 칩 하나다 — 서버가 오더 관리와 같은 사다리로 하나만 골라 준다(`Dispatch.ladder`)
						status: (params: GridRenderCellParams<DispatchType, any, any>) => (
							<span className={boStatusChipClass(params.value)}>
								{DISPATCH_STATUS_TITLE[params.value] ?? params.value}
							</span>
						),
						// 입금일 — 도매 미입금이면 날짜 대신 「미입금」. 입금 전에도 배송은 진행된다(정산에서 입금 확인)
						paidAt: (params: GridRenderCellParams<DispatchType, any, any>) =>
							params.row.channel === 'wholesale' && params.row.paidYn === 0 ? (
								<span className={boStatusChipClass('awaitingPayment')}>미입금</span>
							) : (
								<span>
									{params.value ? dayjs(params.value).format('YYYY-MM-DD') : ''}
								</span>
							),
						totalAmount: (params: GridRenderCellParams<DispatchType, any, any>) => (
							<span className="num">{money(params.row.currency, params.value)}</span>
						),
						trackingNumber: (params: GridRenderCellParams<DispatchType, any, any>) => {
							const url = dispatchTrackUrl(params.row.carrier, String(params.value ?? ''))
							return params.value ? (
								url !== '' ? (
									<a
										href={url}
										target="_blank"
										rel="noreferrer"
										onClick={e => e.stopPropagation()}
									>
										{params.value}
									</a>
								) : (
									<span>{params.value}</span>
								)
							) : (
								<></>
							)
						},
						orderId: (params: GridRenderCellParams<DispatchType, any, any>) =>
							params.value ? (
								<span className="orderCell">
									{params.value}
									{params.row.boxCount > 1 && (
										<span className="boxNo">
											{params.row.boxNo}/{params.row.boxCount}
										</span>
									)}
								</span>
							) : (
								<></>
							),
					}}
					renderEditCell={{}}
				/>
			</BoxView>

			{/* 한 번에 한 겹만 선다 — 라벨 화면이 열리면 자세히는 그리지 않는다 */}
			{isCreateRoute && <DispatchCreate />}
			{dispatchId !== '' && !isLabelRoute && <DispatchDetail />}
			{dispatchId !== '' && isLabelCreate && <ShipmentLabelCreate />}
			{dispatchId !== '' && isLabelView && <ShipmentLabelView />}

			<ShipmentScanModal />
			<ShipmentMessageModal />
		</View>
	)
}

export default ShippingPage

const style = css({
	...boShared,
	height: '100%',
	'.leftView': {
		display: 'flex',
		alignItems: 'center',
		gap: 8,
		flexWrap: 'wrap',
		// 조건이 일곱이라 검색 칸이 셀렉트를 밀어 라벨이 잘렸다 — 셀렉트는 줄이지 않는다
		'.MuiFormControl-root': { flexShrink: 0 },
		'.keywordField': { width: '14rem', flexShrink: 1 },
		'.statusSelect': { minWidth: '8rem' },
		'.kindSelect': { minWidth: '7rem' },
		'.channelSelect': { minWidth: '6rem' },
		'.modeSelect': { minWidth: '5.5rem' },
		'.seasonSelect': { minWidth: '14rem' },
		'.datePickerView': {
			display: 'flex',
			alignItems: 'center',

			'.datePicker': {
				width: '9rem',

				'.MuiInputBase-input': {
					fontSize: 12,
					height: '1.5rem',
				},
			},

			'.datePickerText': {
				margin: '0 8px',
				color: mono.faint,
			},
		},
	},
	'.rightView': { display: 'flex', alignItems: 'center', gap: 4 },
	// 환불된 주문은 배송 상태 칩 옆에 칩이 하나 더 선다
	'.num': { fontVariantNumeric: 'tabular-nums' },
	'.orderCell': { display: 'inline-flex', alignItems: 'center', gap: 6 },
	'.boxNo': {
		fontSize: 11,
		color: mono.faint,
		fontVariantNumeric: 'tabular-nums',
	},
	a: { color: mono.ink },
})
